import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  asapScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  from,
  iif,
  map,
  mergeMap,
  observeOn,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import type {
  Accessibility,
  DisplayConfiguration,
  DisplayVariants,
  SizeClasses,
} from '@plugins/client/button/shared-button';
import { contrastColor } from '@plugins/client/colors/color-utils';
import { AnimationsConfig } from '@plugins/client/directives/animation.directive';
import { ListDirective } from '@plugins/client/directives/list/list.directive';
import { DEFAULT_DISPLAY_LIMIT } from '@plugins/shared/constants/display-limit';
import { Maybe, Nullish } from '@plugins/shared/types';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

import {
  BASE_LINE_HEIGHT_SIZE,
  PICKED_HEIGHT_TO_NAME_RATIO,
} from '../../private/config';
import type {
  FindEntityReturn,
  ValidSearchTypes,
} from '../../private/utils/types.private';
import {
  areArraysEqual,
  generateCompositeSize,
} from '../../private/utils/utils';
import {
  PickedEntitiesService,
  ProfileDataService,
  UserDetailsService,
} from '../services';

import { setLoadSlice } from './user-list.utils';

export type UserListButtonConfig = Partial<{
  color: string;
  class: string;
  variant: DisplayVariants;
  size: SizeClasses;
  style: Record<string, string>;
  aria: Partial<Accessibility>;
  config: DisplayConfiguration;
  round: boolean;
  id: string;
}>;

/** Display a list of user avatars and names, with display limiting turned on by default.

Examples:
```html
<!-- Limits the list by default, based on pre-set value (currently 25) -->
<plugins-user-list [userIds]="listOfIds" searchType="user" />

<!-- Load the list without any display limiting. Any inputs related to lazy loading features are ignored -->
<plugins-user-list [userIds]="listOfIds" [displayLimit]="false" />

<!-- Pass userIds, but prevent loading of entities until allowLoad is true. Useful for modal use cases.-->
<plugins-user-list [userIds]="listOfIds" [allowLoad]="booleanCondition" />

  <!-- Lazy load a list of user ids that are fed additional inputs over time. Use a custom component for triggering new fetches of user data. Handle events. -->

  <plugins-user-list
    [userIds]="initialList"
    [pushIds]="incomingIds$|async"
    [showButton]="false"
    (listEvent)="handleItemClick($event)"
    (loadEvent)="handleLoad($event)" #ref>
      <custom-button-component (clicked)="ref.loadMore()"> Custom Text
      </custom-button-component>
  </plugins-user-list> */
@Component({
  selector: 'plugins-user-list',
  standalone: false,
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  providers: [PickedEntitiesService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--plugins-list-highlight-color]': 'highlightColor()',
    '[style.--plugins-list-item-text-color]': 'contrastTextColor()',
  },
})
export class UserListComponent {
  // Keep old Input decorator until rxjs-interop stable
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set userIds(ids: string[]) {
    const unique = [...new Set(ids)];
    if (areArraysEqual(unique, this.allIds)) return;
    this.#allIds.next(unique);
    this.numberOfLoaded = 0;
  }
  /** Push user ids, that are not given via `userIds` input
   * to the end of the id list without resetting it. */
  // Keep old Input decorator until rxjs-interop stable
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set pushIds(ids: string | string[]) {
    const unique = [...new Set([...this.allIds, ...returnAsArray(ids)])];
    if (areArraysEqual(unique, this.allIds)) return;
    this.#allIds.next(unique);
  }

  /** Remove user ids, that are not given via `userIds` input
   * from the list without resetting it. */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set removeIds(ids: string | string[]) {
    const targetIds = returnAsArray(ids);
    const removedIds: string[] = [];
    const reducedIds = this.allIds.filter((id) => {
      const toBeRemoved = targetIds.includes(id);
      if (toBeRemoved) removedIds.push(id);
      return !toBeRemoved;
    });
    if (!removedIds.length) return;
    this.#allIds.next(reducedIds);
  }

  /** If set to `false`, no entities will be loaded. Useful for modal contexts
   * to prevent automatic loading of passed ids when component is in the
   * template, but not yet visible.
   *
   * Note, that setting this to false will not display the component at all. */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set allowLoad(allow: boolean) {
    this.#allowLoad.next(allow);
  }
  get allowLoad(): boolean {
    return this.#allowLoad.getValue();
  }

  /** Limits the number of displayed entities */
  readonly displayLimit = input(true);

  // Keep old Input decorator until rxjs-interop stable
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set showButton(show: boolean) {
    this.#showButton.next(show);
  }
  get showButton() {
    return this.#showButton.getValue();
  }

  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   *
   * Defaults to `all`. */
  readonly searchType = input<ValidSearchTypes, Maybe<ValidSearchTypes>>(
    'all',
    { transform: (val: Maybe<ValidSearchTypes>) => val ?? 'all' },
  );
  readonly loadLimit = input<number, Maybe<number>>(DEFAULT_DISPLAY_LIMIT, {
    transform: (limit: Maybe<number>) => {
      return limit && limit > 0 ? limit : DEFAULT_DISPLAY_LIMIT;
    },
  });
  /** Optional number of entities to preload on initial load, regardless of
   *  the set `loadLimit`.  */
  readonly preload = input<Maybe<number>>();
  /** Label for the load more button. */
  readonly label = input<Maybe<string>>();

  /* Avatar/Username specific inputs */
  /** Should rendered entities have detailed title */
  readonly detailedTitle = input<Maybe<boolean>>();
  readonly disableAnimations = input<boolean>(false);
  /** Size argument to be passed to the each rendered `UserEntityComponent` */
  readonly size = input<Maybe<string | number>>();

  /** Configuration properties for the `ShardButtonComponent` that is used for the Load more button. */
  readonly buttonConfig = input<UserListButtonConfig | Nullish>({
    class: 'primary',
  });

  /** Background color of the focused/highlighted list item */
  readonly highlightColor = input<string>('#f8f9fa');

  /** Should entities link to user profile page. Defaults to `true` */
  readonly profileLinksEnabled = input(true);

  /** Id and index of the user entity that was clicked or picked
   * via keyboard navigation . */
  readonly listEvent = output<{
    id: string;
    index: number;
  }>();

  /** Event triggered when escape has been pressed. Useful for closing modals or dialogs that contain the user list. */
  readonly escapeEvent = output();

  /** Number of entities loaded in the current load. */
  readonly loadEvent = output<{ loaded: number }>();

  protected userDetailsService = inject(UserDetailsService);
  protected profileDataService = inject(ProfileDataService);
  protected pickedEntitiesService = inject(PickedEntitiesService);

  readonly list = viewChild<ListDirective>('list');
  #allIds = new BehaviorSubject<string[]>([]);
  #numberOfLoaded = new BehaviorSubject(0);
  protected readonly disabled = signal<boolean>(false);
  #triggerLoad = new BehaviorSubject<null | true>(null);

  protected readonly defaultLabelText = 'userDetails.userList.loadMore';

  readonly ListItemAnimations: AnimationsConfig = {
    enter: {
      type: ['fade', 'growY'],
      timingOptions: { duration: 150, fill: 'forwards' },
    },
  };

  readonly loadMoreAnimations: AnimationsConfig = {
    enter: {
      type: ['fade', 'growY', 'drop'],
      timingOptions: { duration: 300, delay: 100, fill: 'forwards' },
    },
  };

  protected readonly isInitial = signal<boolean>(true);
  readonly listItemHeight = computed(
    () =>
      generateCompositeSize(this.size(), PICKED_HEIGHT_TO_NAME_RATIO) ??
      BASE_LINE_HEIGHT_SIZE,
  );
  #allowLoad = new BehaviorSubject<boolean>(true);
  allowLoad$ = this.#allowLoad.asObservable().pipe(distinctUntilChanged());
  #showButton = new BehaviorSubject<boolean>(true);

  showLoadButton$ = combineLatest([
    this.#allIds.asObservable(),
    this.allowLoad$,
    this.#showButton.asObservable().pipe(distinctUntilChanged()),
    this.#numberOfLoaded.asObservable().pipe(distinctUntilChanged()),
  ]).pipe(
    map(() => {
      const hideButton =
        !this.allowLoad ||
        !this.showButton ||
        !this.displayLimit() ||
        this.areAllLoaded ||
        this.loadLimit() >= this.allIds.length;
      if (hideButton) return false;
      return true;
    }),
    startWith(false),
  );

  constructor() {
    combineLatest([this.#triggerLoad, this.#allIds, this.allowLoad$])
      .pipe(
        observeOn(asapScheduler),
        mergeMap(([trigger, allIds]) =>
          iif(
            () => {
              if (!this.allowLoad) return false;
              // Always proceed to load if no entities loaded
              if (!this.numberOfLoaded) return true;
              //Proceed to load if we have ids AND
              // no display limit OR load trigger occurred
              if (!allIds.length) return false;
              if (!this.displayLimit()) return true;
              return !!trigger;
            },
            // CASE: LOAD ENTITIES
            of(1).pipe(
              tap(() => {
                this.handleInitialState(this.numberOfLoaded);
                this.disabled.set(true);
              }),
              switchMap(() => {
                const loadSlice = setLoadSlice({
                  limitOutput: this.displayLimit(),
                  ids: this.allIds,
                  numberOfLoaded: this.numberOfLoaded,
                  loadLimit: this.loadLimit(),
                  preload: this.preload(),
                });
                this.pickedEntitiesService.setIds(loadSlice);

                return from(
                  this.userDetailsService.findEntity(
                    loadSlice,
                    this.searchType(),
                  ),
                ).pipe(
                  tap((entities) => {
                    // Prevent infinite loops if lazy load is disabled and id's get added
                    if (this.#triggerLoad.getValue() !== null) {
                      this.#triggerLoad.next(null);
                    }
                    this.disabled.set(false);
                    this.updateEntitiesMap(entities);
                    this.loadEvent.emit({ loaded: this.numberOfLoaded });
                  }),
                );
              }),
            ),
            // CASE: NON LOAD
            of(1).pipe(
              tap(() => {
                this.updateEntitiesMap();
              }),
            ),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  get allIds(): string[] {
    return this.#allIds.getValue();
  }

  get numberOfLoaded(): number {
    return this.#numberOfLoaded.getValue();
  }

  set numberOfLoaded(num: number) {
    this.#numberOfLoaded.next(num);
  }

  get areAllLoaded(): boolean {
    return this.numberOfLoaded >= this.allIds.length;
  }

  get triggerLoad(): boolean | null {
    return this.#triggerLoad.getValue();
  }

  loadMore(): void {
    if (this.areAllLoaded) {
      return;
    }
    this.#triggerLoad.next(true);
  }

  itemSelected(id: string, index: number): void {
    this.listEvent.emit({ id, index });
  }

  itemClick(event: MouseEvent, id: string, index: number): void {
    if (this.profileLinksEnabled()) {
      this.openUserProfile(event, id);
    } else {
      this.listEvent.emit({ id, index });
    }
  }

  itemKeydown(event: KeyboardEvent, id: string, index: number) {
    if (!['Escape', 'Enter', ' '].includes(event.key)) {
      return;
    }
    if (event.key === 'Escape') {
      this.escapeEvent.emit();
      return;
    }
    if (this.profileLinksEnabled()) {
      this.openUserProfile(event, id);
      return;
    }
    this.listEvent.emit({ id, index });
  }

  /** Computed text color that contrasts with highlightColor */
  protected readonly contrastTextColor = computed(() =>
    contrastColor('#000000', {
      contrastingColor: this.highlightColor(),
      lowContrastReturn: '#ffffff',
      highContrastReturn: '#000000',
    }),
  );

  private openUserProfile(event: Event, userId: string) {
    event.preventDefault();
    event.stopPropagation();
    void this.profileDataService.openProfile(userId);
  }

  private updateEntitiesMap(
    entities: FindEntityReturn<ValidSearchTypes> = [],
  ): void {
    if (entities.length) {
      this.pickedEntitiesService.setEntityValue(entities);
    }
    /** If ids are removed, drop them from picked service map */
    this.pickedEntitiesService.removeIds(
      this.pickedEntitiesService.pickedIds.filter(
        (id) => !this.allIds.includes(id),
      ),
    );

    // In the future this could only count valid entities to allow refetching failed requests.
    this.numberOfLoaded = this.pickedEntitiesService.entities.length;
  }

  private handleInitialState(loaded: number): void {
    const shouldBeInitial = !loaded;
    if (this.isInitial() !== shouldBeInitial) {
      this.isInitial.set(shouldBeInitial);
    }
  }
}
